/**
 * Mirrors `services/engine/src/api/routes/ludus-leads.ts` — one sale, whole.
 *
 *   GET /ludus-leads/:id → { view, leads, pipelines } | 404
 *
 * Same posture as `pipelines.ts` and `flows.ts`: Zod at the network boundary,
 * `cast*` helpers, one typed error. Two things are worth stating because they
 * differ from the neighbours:
 *
 * - **Timestamps arrive as ISO strings, not epoch millis.** The route returns
 *   the pure `LudusLeadView` and Hono serialises its `Date` fields directly,
 *   where `flows.ts` reads numbers because `flows/flows.ts` projects every date
 *   through `.getTime()` first. Both are cast to `Date` here, so the console
 *   still sees one convention — but the schemas cannot be copied between the
 *   two files, and this note is why.
 * - **The response is a view plus the rows its ids name.** The layer's own
 *   value is ids-only on purpose (ADR-0005 §5 refuses a second lead schema), so
 *   the route composes `leads` and `pipelines` maps beside it. `hasAdapter`
 *   rides along because a mirrored board is read-only, and the console has to
 *   know which sibling card can be written back to (D6).
 */

import { z } from 'zod'
import {
  asLeadId,
  asLudusLeadId,
  asPipelineId,
  type Lead,
  type LudusLeadId,
  type PipelineKind,
} from '@ludus/core'
import { apiClient } from './client.ts'
import { castLead, leadSchema } from './pipelines.ts'

// ──────────────────────────────────────────────────────────────────────────────
// Schemas
// ──────────────────────────────────────────────────────────────────────────────

/**
 * `confidence` and `method` are read as bare strings for the reason
 * `engineRole` is in `pipelines.ts`: a value this build has no word for must
 * still render, because refusing the response would blank a page whose whole
 * job is to show what the layer decided.
 */
const cardLinkSchema = z.object({
  leadId: z.string(),
  ludusLeadId: z.string(),
  confidence: z.string(),
  method: z.string(),
  linkedBy: z.string(),
  ruleVersion: z.string(),
  evidence: z.record(z.unknown()).optional(),
})

const pipelineCardSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  pipelineKind: z.string(),
  occurredAt: z.string(),
  closedAt: z.string().nullable(),
  productKey: z.string().nullable(),
  identifiers: z.array(z.unknown()).optional(),
})

const linkedCardSchema = z.object({ card: pipelineCardSchema, link: cardLinkSchema })

const viewSchema = z.object({
  ludusLead: z.object({
    id: z.string(),
    customerId: z.string(),
    productKey: z.string().nullable(),
    openedAt: z.string(),
    closedAt: z.string().nullable(),
    lastCardAt: z.string(),
    origin: z.string(),
    anomalyFlags: z.array(z.string()),
    ruleVersion: z.string(),
  }),
  customer: z.object({
    id: z.string(),
    anomaly: z.string(),
    createdBy: z.string(),
    mergedInto: z.string().nullable(),
    createdAt: z.string(),
  }),
  identifiers: z.array(
    z.object({
      kind: z.string(),
      valueNormalized: z.string(),
      valueRaw: z.string(),
      confidence: z.number().optional(),
    }),
  ),
  pipelines: z.array(
    z.object({
      pipelineId: z.string(),
      pipelineKind: z.string(),
      cards: z.array(linkedCardSchema),
      attributed: z.number(),
      disputed: z.number(),
    }),
  ),
  cards: z.array(linkedCardSchema),
  coverage: z.object({
    cards: z.number(),
    dealCards: z.number(),
    interactionCards: z.number(),
    pipelines: z.number(),
    disputedLinks: z.number(),
    firstCardAt: z.string().nullable(),
    lastCardAt: z.string().nullable(),
  }),
  anomalyFlags: z.array(z.string()),
})

const saleSchema = z.object({
  view: viewSchema,
  leads: z.record(z.unknown()),
  pipelines: z.record(
    z.object({
      id: z.string(),
      name: z.string(),
      kind: z.string(),
      hasAdapter: z.boolean(),
    }),
  ),
})

const salesListSchema = z.object({
  items: z.array(viewSchema),
  leads: z.record(z.unknown()),
  pipelines: z.record(
    z.object({
      id: z.string(),
      name: z.string(),
      kind: z.string(),
      hasAdapter: z.boolean(),
    }),
  ),
  statuses: z.record(
    z.object({ id: z.string(), name: z.string(), lifecyclePhase: z.string().nullable() }),
  ),
})

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface SaleCardLink {
  leadId: string
  confidence: string
  method: string
  linkedBy: string
}

export interface SaleCard {
  id: string
  pipelineId: string
  pipelineKind: string
  occurredAt: Date
  closedAt: Date | null
  link: SaleCardLink
}

export interface SalePipelinePresence {
  pipelineId: string
  pipelineKind: string
  cards: SaleCard[]
  attributed: number
  disputed: number
}

export interface SaleCoverage {
  cards: number
  dealCards: number
  interactionCards: number
  pipelines: number
  disputedLinks: number
  firstCardAt: Date | null
  lastCardAt: Date | null
}

export interface SaleIdentifier {
  kind: string
  value: string
}

export interface SalePipelineRow {
  id: string
  name: string
  kind: PipelineKind
  /** Whether this board is mirrored from a source — a mirrored board is read-only (D6). */
  hasAdapter: boolean
}

export interface Sale {
  id: LudusLeadId
  productKey: string | null
  openedAt: Date
  closedAt: Date | null
  origin: string
  anomalyFlags: string[]
  identifiers: SaleIdentifier[]
  pipelines: SalePipelinePresence[]
  /** Every card, oldest first — the sale as one timeline. */
  cards: SaleCard[]
  coverage: SaleCoverage
  /** The card rows the view's ids name, so a card renders without an N+1. */
  leads: Record<string, Lead>
  boards: Record<string, SalePipelineRow>
}

/** One row of the sales list: a sale and, per board, the cards it holds with their stage. */
export interface SaleSummaryCard {
  id: string
  name: string
  pipelineId: string
  statusId: string
  statusName: string | null
  lifecyclePhase: string | null
  confidence: string
}

export interface SaleSummary {
  id: LudusLeadId
  openedAt: Date
  closedAt: Date | null
  lastCardAt: Date
  anomalyFlags: string[]
  identifiers: SaleIdentifier[]
  /** Every board holding a card of this sale, deals first, with the cards on it. */
  boards: Array<{ id: string; name: string; hasAdapter: boolean; cards: SaleSummaryCard[] }>
  cards: number
}

export class SaleApiError extends Error {
  readonly kind: 'not_found' | 'unauthorized' | 'network' | 'malformed_response' | 'unknown'
  readonly status: number | null

  constructor(init: { kind: SaleApiError['kind']; status: number | null; message: string }) {
    super(init.message)
    this.name = 'SaleApiError'
    this.kind = init.kind
    this.status = init.status
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Casts
// ──────────────────────────────────────────────────────────────────────────────

const date = (iso: string): Date => new Date(iso)
const dateOrNull = (iso: string | null): Date | null => (iso === null ? null : new Date(iso))

const castLinkedCard = (row: z.infer<typeof linkedCardSchema>): SaleCard => ({
  id: row.card.id,
  pipelineId: row.card.pipelineId,
  pipelineKind: row.card.pipelineKind,
  occurredAt: date(row.card.occurredAt),
  closedAt: dateOrNull(row.card.closedAt),
  link: {
    leadId: row.link.leadId,
    confidence: row.link.confidence,
    method: row.link.method,
    linkedBy: row.link.linkedBy,
  },
})

function classifySaleError(err: unknown): SaleApiError {
  if (err instanceof z.ZodError) {
    // Tested before the transport branch: a `ZodError` has no `.response`, so
    // reading `status` off it would call a contract mismatch a network fault.
    return new SaleApiError({
      kind: 'malformed_response',
      status: null,
      message: 'the engine answered a shape this build does not understand',
    })
  }
  const status =
    typeof err === 'object' && err !== null && 'response' in err
      ? ((err as { response?: { status?: number } }).response?.status ?? null)
      : null
  if (status === 404) {
    return new SaleApiError({ kind: 'not_found', status, message: 'sale not found' })
  }
  if (status === 401 || status === 403) {
    return new SaleApiError({ kind: 'unauthorized', status, message: 'unauthorized' })
  }
  if (status === null) {
    return new SaleApiError({ kind: 'network', status, message: 'the engine is unreachable' })
  }
  return new SaleApiError({ kind: 'unknown', status, message: `the engine answered ${status}` })
}

// ──────────────────────────────────────────────────────────────────────────────
// Fetchers
// ──────────────────────────────────────────────────────────────────────────────

export async function fetchSale(ludusLeadId: LudusLeadId): Promise<Sale> {
  try {
    const { data } = await apiClient.get(`/ludus-leads/${encodeURIComponent(ludusLeadId)}`)
    const parsed = saleSchema.parse(data)
    const { view } = parsed

    const leads: Record<string, Lead> = {}
    for (const [id, row] of Object.entries(parsed.leads)) {
      // Parsed and cast by `pipelines.ts`'s own pair, so a card on this page is
      // the same shape the kanban and the lead detail already render. A second
      // reading of one row is how two pages start disagreeing about one card.
      leads[id] = castLead(leadSchema.parse(row))
    }

    const boards: Record<string, SalePipelineRow> = {}
    for (const [id, row] of Object.entries(parsed.pipelines)) {
      boards[id] = {
        id: String(asPipelineId(row.id)),
        name: row.name,
        kind: row.kind as PipelineKind,
        hasAdapter: row.hasAdapter,
      }
    }

    return {
      id: asLudusLeadId(view.ludusLead.id),
      productKey: view.ludusLead.productKey,
      openedAt: date(view.ludusLead.openedAt),
      closedAt: dateOrNull(view.ludusLead.closedAt),
      origin: view.ludusLead.origin,
      anomalyFlags: [...view.anomalyFlags],
      identifiers: view.identifiers.map((i) => ({ kind: i.kind, value: i.valueRaw })),
      pipelines: view.pipelines.map((p) => ({
        pipelineId: p.pipelineId,
        pipelineKind: p.pipelineKind,
        cards: p.cards.map(castLinkedCard),
        attributed: p.attributed,
        disputed: p.disputed,
      })),
      cards: view.cards.map(castLinkedCard),
      coverage: {
        ...view.coverage,
        firstCardAt: dateOrNull(view.coverage.firstCardAt),
        lastCardAt: dateOrNull(view.coverage.lastCardAt),
      },
      leads,
      boards,
    }
  } catch (err) {
    throw classifySaleError(err)
  }
}

/**
 * Every sale, newest activity first — `GET /ludus-leads`. One request for the
 * whole page: the views plus the card, board and stage rows their ids name.
 */
export async function fetchSales(limit = 50): Promise<SaleSummary[]> {
  try {
    const { data } = await apiClient.get(`/ludus-leads?limit=${limit}`)
    const parsed = salesListSchema.parse(data)
    const leads: Record<string, Lead> = {}
    for (const [id, row] of Object.entries(parsed.leads))
      leads[id] = castLead(leadSchema.parse(row))

    return parsed.items.map((view) => ({
      id: asLudusLeadId(view.ludusLead.id),
      openedAt: date(view.ludusLead.openedAt),
      closedAt: dateOrNull(view.ludusLead.closedAt),
      lastCardAt: date(view.ludusLead.lastCardAt),
      anomalyFlags: [...view.anomalyFlags],
      identifiers: view.identifiers.map((i) => ({ kind: i.kind, value: i.valueRaw })),
      boards: view.pipelines.map((presence) => {
        const board = parsed.pipelines[presence.pipelineId]
        return {
          id: presence.pipelineId,
          name: board?.name ?? presence.pipelineId,
          hasAdapter: board?.hasAdapter ?? false,
          cards: presence.cards.map(({ card, link }) => {
            const lead = leads[card.id]
            const status = lead ? parsed.statuses[lead.statusId] : undefined
            return {
              id: card.id,
              name: lead?.name ?? card.id,
              pipelineId: card.pipelineId,
              statusId: lead?.statusId ?? '',
              statusName: status?.name ?? null,
              lifecyclePhase: status?.lifecyclePhase ?? null,
              confidence: link.confidence,
            }
          }),
        }
      }),
      cards: view.cards.length,
    }))
  } catch (err) {
    throw classifySaleError(err)
  }
}

/** The id a card carries when it sits on a cycle, narrowed for a route param. */
export const asSaleId = (raw: string): LudusLeadId => asLudusLeadId(raw)

/** Re-exported so a caller can narrow a card id without importing core directly. */
export { asLeadId }
